import { useState, useEffect, useMemo, useContext } from 'react';
import { AppContext } from '../../../context/AppContext';
import { Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Select from 'react-select';
import { creditRewardSchema } from '../../../schemas/wallet/admin.schema';
import { useAdmin } from '../../../Hooks/wallet/useadmin';
import { getCurrencies } from '../../../api/wallet/wallet.api';

const ALL_OPTION = { value: '__all__', label: 'All Users' };

const RewardCreditPage = () => {
    const { sidebar } = useContext(AppContext);
    const { loading, doCreditReward, fetchUsers } = useAdmin();
    const [currencies, setCurrencies] = useState([]);
    const [users, setUsers] = useState([]);

    const { register, handleSubmit, reset, control, formState: { errors } } = useForm({
        resolver: zodResolver(creditRewardSchema),
        defaultValues: { user_id: [] },
    });

    useEffect(() => {
        const load = async () => {
            try {
                const [currRes, userRes] = await Promise.all([
                    getCurrencies(),
                    fetchUsers({ limit: 1000 }),
                ]);
                setCurrencies(currRes?.data || []);
                setUsers(userRes?.data?.users || []);
            } catch {
                // handled
            }
        };
        load();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const userOptions = useMemo(
        () => users.map((u) => ({ value: u.id, label: `#${u.id} — ${u.name || u.email}` })),
        [users]
    );

    const onSubmit = async (data) => {
        try {
            await doCreditReward(data);
            reset();
        } catch {
            // handled
        }
    };

    return (
        <div className={sidebar ? "taskMain " : "cx-active taskMain"} id="cx-main">
            <div className="role-content-main">
            <div className="sub-header-role mb-4">
                <h6 className="mt-2 role-text">Reward Credit</h6>
            </div>
                <div className="fade-in">
            

            <Card className="border-0 shadow-sm" style={{ maxWidth: 600 }}>
                <Card.Body className="p-4">
                    <Alert variant="info" className="mb-3">
                        If a selected user has no reward wallet, one will be auto-created.
                    </Alert>

                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <Form.Group className="mb-3">
                            <Form.Label>Users</Form.Label>
                            <Controller
                                name="user_id"
                                control={control}
                                render={({ field }) => {
                                    const allSelected =
                                        field.value?.length === userOptions.length && userOptions.length > 0;

                                    const selectValue = allSelected
                                        ? [ALL_OPTION]
                                        : userOptions.filter((o) => field.value?.includes(o.value));

                                    const options = [ALL_OPTION, ...userOptions];

                                    const handleChange = (selected) => {
                                        if (!selected || selected.length === 0) {
                                            field.onChange([]);
                                            return;
                                        }
                                        const hasAll = selected.some((o) => o.value === '__all__');
                                        if (hasAll) {
                                            field.onChange(userOptions.map((o) => o.value));
                                        } else {
                                            field.onChange(selected.map((o) => o.value));
                                        }
                                    };

                                    return (
                                        <Select
                                            isMulti
                                            options={options}
                                            value={selectValue}
                                            onChange={handleChange}
                                            placeholder="Select users…"
                                            isLoading={loading && users.length === 0}
                                            classNamePrefix="rselect"
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    borderColor: errors.user_id
                                                        ? '#dc3545'
                                                        : state.isFocused
                                                        ? '#86b7fe'
                                                        : '#dee2e6',
                                                    boxShadow: errors.user_id
                                                        ? '0 0 0 0.25rem rgba(220,53,69,.25)'
                                                        : state.isFocused
                                                        ? '0 0 0 0.25rem rgba(13,110,253,.25)'
                                                        : 'none',
                                                    '&:hover': { borderColor: errors.user_id ? '#dc3545' : '#86b7fe' },
                                                }),
                                            }}
                                        />
                                    );
                                }}
                            />
                            {errors.user_id && (
                                <div className="text-danger small mt-1">{errors.user_id.message}</div>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Amount</Form.Label>
                            <Form.Control type="number" step="0.01" placeholder="Enter amount" isInvalid={!!errors.amount} {...register('amount')} />
                            <Form.Control.Feedback type="invalid">{errors.amount?.message}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Currency</Form.Label>
                            <Form.Select isInvalid={!!errors.currency_code} {...register('currency_code')}>
                                <option value="">Select currency</option>
                                {currencies.map((c) => (
                                    <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
                                ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">{errors.currency_code?.message}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Description <span className="text-muted">(optional)</span></Form.Label>
                            <Form.Control type="text" placeholder="e.g., Welcome bonus" {...register('description')} />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Reference ID <span className="text-muted">(optional)</span></Form.Label>
                            <Form.Control type="text" {...register('reference_id')} />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Reference Type <span className="text-muted">(optional)</span></Form.Label>
                            <Form.Select {...register('reference_type')}>
                                <option value="">Select reference type</option>
                                <option value="bank_transfer">Bank Transfer</option>
                                <option value="check">Check</option>
                                <option value="cash">Cash</option>
                                <option value="other">Other</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Expires At <span className="text-muted">(optional)</span></Form.Label>
                            <Form.Control type="datetime-local" {...register('expires_at')} />
                        </Form.Group>

                        <Button type="submit" variant="primary" disabled={loading} className="w-100">
                            {loading ? <Spinner size="sm" /> : 'Credit Reward'}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>
        </div>
        </div>
        </div>
    );
};

export default RewardCreditPage;
